// main components
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { FreeFormInput } from './FreeFormInput'

import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// mui components
import { Drawer, FormLabel, Box, Typography, Grid, Button } from '@mui/material'
import { Selector } from './Selector'
import { store } from '../../store'
import { authActions } from '../../store/signIn'
import * as ApiServices from '../../services/ApiService'
import { PhoneInput } from './PhoneInput'
import { isEqual } from 'lodash'
import { RoundedButton } from '../../styles/mui_custom_components'
import { useSelector } from 'react-redux'
import { adminRole, apiErrors } from '../../lib/Constants'
import { ConfirmDialog } from '../form/ConfirmDialog'

// Styles
import { UpdateAccountInfoClasses } from '../../styles/classes/AccountSettingsClasses'

// Services
import {
  getUserAndRoles,
  updateAccountSettings
} from '../../services/ApiService'
import { userHasAuthorization } from '../../services/AuthService'

export const UpdateAccountInfo = props => {
  const {
    editDrawer,
    handleClosePanel,
    accountInfo,
    roles,
    event,
    updateUsers,
    affiliateId,
    accountOwner
  } = props

  const { classes } = UpdateAccountInfoClasses()
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState(false)
  const [enableSave, setEnableSave] = useState()
  const [passwordChange, setpasswordChange] = useState(false)
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false)
  const [openConfirm, setOpenConfirm] = useState(false)
  const [disableDeleteUser, setDisableDeleteUser] = useState(false)
  const [disableRole, setDisableRole] = useState()
  const [openAlert, setOpenAlert] = useState(false)
  const [popupMessage, setPopupMessage] = useState()
  const userStore = useSelector(state => state.auth.user)

  // inline styles
  const styles = {
    emailNotifications: {
      display: 'none'
    },
    employeeId: {
      display: 'none'
    }
  }

  const passwordPlaceHolder = '******'
  const startingInfo = {
    firstName: accountInfo.userInfo.firstName,
    lastName: accountInfo.userInfo.lastName,
    email: accountInfo.userInfo.email,
    phone: accountInfo.userInfo.phone,
    username: accountInfo.userInfo.username,
    photo_url: accountInfo.userInfo.photo_url,
    roles: accountInfo.userInfo.roles,
    role: accountInfo.userInfo.role,
    password: passwordPlaceHolder,
    passwordConfirm: ''
  }
  const [updatedInfo, setUpdatedInfo] = useState({ ...startingInfo })

  useEffect(() => {
    setDisableDeleteUser(
      userStore?.userInfo?.email === accountInfo.userInfo.email
    )
    setDisableRole(userStore?.userInfo?.scopes?.name !== adminRole)
  }, [])

  useEffect(() => {
    if (editDrawer) {
      try {
        setOpen(true)
      } catch (err) {
        console.error(err)
      }
    }
  }, [editDrawer])

  const handleClose = u => {
    setOpen(false)
    handleClosePanel(accountInfo)
  }

  /** VALIDATIONS **/
  const validationSchema = yup.object().shape({
    firstName: yup
      .string()
      .required(t('account_settings.messages.errors.required')),
    lastName: yup
      .string()
      .required(t('account_settings.messages.errors.required')),
    phone: yup
      .string()
      .trim()
      .min(10, t('general.messages.errors.phone')),
    email: yup
      .string()
      .required(t('account_settings.messages.errors.required'))
      .email(t('account_settings.messages.errors.email'))
  })

  const validationSchemaNew = yup.object().shape({
    firstName: yup
      .string()
      .required(t('account_settings.messages.errors.required')),
    lastName: yup
      .string()
      .required(t('account_settings.messages.errors.required')),
    phone: yup
      .string()
      .trim()
      .min(10, t('general.messages.errors.phone')),
    email: yup
      .string()
      .required(t('account_settings.messages.errors.required'))
      .email(t('account_settings.messages.errors.email')),
    password: yup
      .string()
      .required(t('account_settings.messages.errors.required'))
      .min(6, t('general.messages.errors.length_6')),
    passwordConfirm: yup
      .string()
      .required(t('account_settings.messages.errors.required'))
      .min(6, t('general.messages.errors.length_6'))
      .oneOf(
        [yup.ref('password'), null],
        t('account_settings.messages.errors.password_match')
      )
  })

  /** End VALIDATIONS **/

  /** Submit Handle **/

  const {
    register,
    handleSubmit,
    reset,
    clearErrors,
    formState: { errors }
  } = useForm({
    mode: 'all',
    resolver:
      event === 'new'
        ? yupResolver(validationSchemaNew)
        : yupResolver(validationSchema)
  })

  useEffect(() => {
    let save = true
    if (event === 'new') {
      if (
        !updatedInfo.firstName ||
        !updatedInfo.lastName ||
        !updatedInfo.email ||
        !updatedInfo.password ||
        (updatedInfo.password && !updatedInfo.passwordConfirm) ||
        (!updatedInfo.password && updatedInfo.passwordConfirm) ||
        (!updatedInfo.password && !updatedInfo.passwordConfirm) ||
        errors?.email?.message ||
        errors?.password?.message ||
        errors?.passwordConfirm?.message ||
        updatedInfo.password !== updatedInfo.passwordConfirm ||
        !updatedInfo?.roles
      ) {
        save = false
      }
    } else {
      if (
        !updatedInfo.firstName ||
        !updatedInfo.lastName ||
        !updatedInfo.email ||
        !updatedInfo.roles ||
        !updatedInfo.password ||
        errors?.password?.message
      ) {
        save = false
      }
    }
    if (isEqual(updatedInfo, startingInfo)) save = false

    if (!userHasAuthorization('users:write')) save = false
    setEnableSave(save)
  }, [updatedInfo, errors])

  const onSubmit = data => {
    handleChangeUser()
  }

  const handleChangeValues = event => {
    let value = event.target.value
    if (event.target.name !== 'phone') value = value.replace(/\s/g, '')
    if (event.target.name === 'firstName' || event.target.name === 'lastName') value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    setUpdatedInfo(prevState => ({
      ...prevState,
      [event.target.name]: value
    }))
    if (event.target.name === 'password') setpasswordChange(true)
  }

  const handleChangeUser = async () => {
    // for new user handling
    if (event === 'new') {
      await handleNewUser()
      return
    }
    // for edit user handling
    if (event === 'edit') {
      await handleUpdateUser()
      return
    }
    // for account details handling
    try {
      setErrorMessage(null)
      clearErrors()
      let newData = {
        firstName: updatedInfo.firstName,
        lastName: updatedInfo.lastName,
        email: updatedInfo.email,
        phone: updatedInfo.phone,
        username: updatedInfo.username,
        photo_url: updatedInfo.photo_url,
        roles: updatedInfo.roles === 'no_value' ? '' : updatedInfo.roles,
        role: updatedInfo.role,
        employee_id: updatedInfo.employeeId
      }

      if (updatedInfo.password && passwordChange) {
        newData = { ...newData, password: updatedInfo.password }
      }

      await updateAccountSettings(newData)
      setOpen(false)

      const response = await getUserAndRoles()
      const newUserData = JSON.parse(JSON.stringify(accountInfo))

      newUserData.userInfo.firstName = newData.firstName
      newUserData.userInfo.lastName = newData.lastName
      newUserData.userInfo.email = newData.email
      newUserData.userInfo.phone = newData.phone
      newUserData.userInfo.username = newData.username
      newUserData.userInfo.photo_url = newData.photo_url
      newUserData.userInfo.roles = newData.roles
      newUserData.userInfo.role = newData.role
      newUserData.userInfo.password = newData.password
      newUserData.userInfo.employee_id = newData.employee_id

      store.dispatch(authActions.setUser(newUserData))
      store.dispatch(
        authActions.setUserRole(response.roles[response.userRoleIndex])
      )
      handleClosePanel(newUserData)
    } catch (e) {
      console.error(e)
      if (e.code === 400) {
        setErrorMessage(e.message)
      } else {
        setErrorMessage(e)
      }
    }
  }

  const handleNewUser = async () => {
    try {
      setErrorMessage(null)
      clearErrors()

      await ApiServices.createClientUser(
        affiliateId,
        updatedInfo.firstName,
        updatedInfo.lastName,
        updatedInfo.email,
        updatedInfo.phone,
        updatedInfo.username,
        updatedInfo.photo_url,
        updatedInfo.roles,
        updatedInfo.role,
        updatedInfo.password,
        updatedInfo.employeeId
      )
      updateUsers()
      handleClose()
    } catch (error) {
      switch (error) {
        case apiErrors.USER_DELETED:
          setOpenAlert(true)
          setPopupMessage(t('general.messages.invite_user'))
          break
        default:
          setErrorMessage(error)
          break
      }
    }
  }

  const handleUpdateUser = async () => {
    try {
      let info = {
        firstName: updatedInfo.firstName,
        lastName: updatedInfo.lastName,
        email: updatedInfo.email,
        phone: updatedInfo.phone,
        username: updatedInfo.username,
        photo_url: updatedInfo.photo_url,
        roles: updatedInfo.roles === 'no_value' ? '' : updatedInfo.roles,
        role: updatedInfo.role,
        employee_id: updatedInfo.employeeId
      }
      if (passwordChange) {
        info = { ...info, password: updatedInfo.password }
      }
      setErrorMessage(null)
      clearErrors()
      const response = await ApiServices.updateClientUser(accountInfo.userInfo.id, info)
      if (response) {
        updateUsers()
        handleClose()
      }
    } catch (e) {
      console.error(e)
      if (e.details && e.details?.details?.length > 0) {
        const error = e?.details?.details?.[0]
        const name =
          error.path.substr(1, error.path.length - 1) === 'role'
            ? 'title'
            : error.path.substr(1, error.path.length - 1)
        setErrorMessage(name + ' ' + error.message)
      } else if (e.message) {
        setErrorMessage(e.message)
      } else setErrorMessage(e)
    }
  }

  const handleDeleteUser = async () => {
    try {
      const deleteResponse = await ApiServices.deleteUser(
        accountInfo.userInfo.id,
        userStore?.userInfo?.company_id
      )
      setOpenDeleteAlert(false)
      setOpenConfirm(deleteResponse)
    } catch (e) {
      console.error('Delete user failed with error: ', e)
    }
  }

  const onError = (errors, e) => console.error(errors, e)

  const hanldleAccept = async () => {
    setOpenAlert(false)
    await ApiServices.sendPortalInvitation(
      updatedInfo.email,
      userStore?.userInfo?.company_id
    )
    updateUsers()
    handleClose()
  }
  /** End Submit Handle **/

  return (
    <div>
      <Drawer
        BackdropProps={{ invisible: true }}
        anchor={'right'}
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.drawerPaper }}
        disableAutoFocus
      >
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit, onError)}
          onReset={reset}
        >
          <div className={classes.drawerContainer}>
            <div className={classes.drawerTitle}>
              <FormLabel component="legend" className={classes.title}>
                {event === 'new'
                  ? t('account_settings.info_card.new_user_title')
                  : event === 'edit'
                    ? t('account_settings.info_card.edit_user_title')
                    : t('account_settings.info_card.title')}
              </FormLabel>
            </div>

            <Grid container p={3} pt={0}>
              <Grid item xs={12}>
                <Grid container mt={2}>
                  <Grid item xs>
                    <FreeFormInput
                      className={classes.redAsterisk}
                      required={event === 'new'}
                      value={updatedInfo.firstName}
                      id="firstName"
                      name="firstName"
                      handleChange={handleChangeValues}
                      label={t('account_settings.info_card.first_name')}
                      placeholder={t(
                        'account_settings.info_card.placeholder_first'
                      )}
                      error={!!errors.firstName}
                      helperText={errors.firstName && errors.firstName.message}
                      {...register('firstName')}
                      endAdornment={userHasAuthorization('users:write')}
                      inputStyle={{
                        width: '100%',
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <div className={classes.separator} />
                  <Grid item xs>
                    <FreeFormInput
                      endAdornment={userHasAuthorization('users:write')}
                      className={classes.redAsterisk}
                      required={event === 'new'}
                      value={updatedInfo.lastName}
                      id="lastName"
                      name="lastName"
                      handleChange={handleChangeValues}
                      label={t('account_settings.info_card.last_name')}
                      placeholder={t(
                        'account_settings.info_card.placeholder_last'
                      )}
                      error={!!errors.lastName}
                      helperText={errors.lastName && errors.lastName.message}
                      {...register('lastName')}
                      inputStyle={{
                        width: '100%',
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <FreeFormInput
                      endAdornment={true}
                      className={classes.redAsterisk}
                      required={event === 'new'}
                      value={updatedInfo.email}
                      id="email"
                      name="email"
                      handleChange={handleChangeValues}
                      label={t('account_settings.info_card.email')}
                      placeholder={t(
                        'account_settings.info_card.placeholder_email'
                      )}
                      error={!!errors.email}
                      helperText={errors.email && errors.email.message}
                      {...register('email')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <PhoneInput
                      value={updatedInfo.phone}
                      id="phone"
                      name="phone"
                      handleChange={handleChangeValues}
                      label={t('account_settings.info_card.phone_number')}
                      placeholder={t(
                        'account_settings.info_card.placeholder_phone'
                      )}
                      error={!!errors.phone}
                      helperText={errors.phone && errors.phone.message}
                      {...register('phone')}
                      endAdornment={userHasAuthorization('users:write')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <Selector
                      required={event === 'new'}
                      id={'roles'}
                      value={
                        roles &&
                        updatedInfo.roles &&
                        roles.find(role => role.id === updatedInfo.roles)
                          ? roles.find(role => role.id === updatedInfo.roles)
                            .name
                          : ''
                      }
                      label={t('company_settings.users_card.panel_access')}
                      handleChange={handleChangeValues}
                      placeholder={t(
                        'account_settings.info_card.placeholder_select'
                      )}
                      options={roles && roles.length > 0 ? [...roles] : []}
                      error={!!errors.roles}
                      helperText={errors.roles && errors.roles.message}
                      disabled={accountOwner}
                      {...register('roles')}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <Selector
                      id={'role'}
                      value={
                        t('request_access.user_roles', {
                          returnObjects: true
                        })[updatedInfo?.role ?? '']
                      }
                      label={t('company_settings.users_card.panel_role')}
                      placeholder={t(
                        'account_settings.info_card.placeholder_select'
                      )}
                      handleChange={handleChangeValues}
                      options={Object.keys(
                        t('request_access.user_roles', { returnObjects: true })
                      ).map(key => {
                        return {
                          id: key,
                          name: t('request_access.user_roles', {
                            returnObjects: true
                          })[key]
                        }
                      })}
                      error={!!errors.role}
                      helperText={errors.role && errors.role.message}
                      {...register('role')}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2} sx={styles.emailNotifications}>
                  <Grid item xs={12}>
                    <Selector
                      id={'emailNotifications'}
                      value={
                        t('create_account.email_notifications', {
                          returnObjects: true
                        })[updatedInfo?.emailNotifications ?? '']
                      }
                      label={t('company_settings.users_card.notifications')}
                      handleChange={handleChangeValues}
                      options={Object.keys(
                        t('create_account.email_notifications', {
                          returnObjects: true
                        })
                      ).map(key => {
                        return {
                          id: key,
                          name: t('create_account.email_notifications', {
                            returnObjects: true
                          })[key]
                        }
                      })}
                      {...register('emailNotifications')}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <FreeFormInput
                      value={updatedInfo.employeeId}
                      id="employeeId"
                      name="employeeId"
                      handleChange={handleChangeValues}
                      label={t('account_settings.info_card.employee_id')}
                      placeholder={t(
                        'account_settings.info_card.placeholder_employee'
                      )}
                      error={!!errors.employeeId}
                      helperText={
                        errors.employeeId && errors.employeeId.message
                      }
                      endAdornment={true}
                      {...register('employeeId')}
                      inputStyle={{
                        width: '100%'
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    <FreeFormInput
                      className={classes.redAsterisk}
                      required={event === 'new'}
                      value={updatedInfo.username}
                      id="username"
                      name="username"
                      handleChange={handleChangeValues}
                      label={`${t('account_settings.info_card.username')} ${t(
                        'account_settings.form.username_chars'
                      )}`}
                      placeholder={t(
                        'account_settings.info_card.placeholder_username'
                      )}
                      error={!!errors.username}
                      helperText={errors.username && errors.username.message}
                      {...register('username')}
                      endAdornment={true}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Grid container mt={2}>
                  <Grid item xs={12}>
                    {event === 'new'
                      ? (
                      <FreeFormInput
                        required={event === 'new'}
                        defaultValue={
                          event === 'new' ? '' : updatedInfo?.password
                        }
                        id="password"
                        name="password"
                        type="password"
                        placeholder={t(
                          'account_settings.info_card.placeholder_pass'
                        )}
                        handleChange={handleChangeValues}
                        label={t('account_settings.info_card.password')}
                        error={!!errors.password}
                        helperText={errors.password && errors.password.message}
                        endAdornment={userHasAuthorization('users:write')}
                        {...register('password')}
                        autoComplete="new-password"
                        InputLabelProps={{ shrink: true }}
                      />
                        )
                      : (
                      <FreeFormInput
                        className={classes.redAsterisk}
                        required={event === 'new'}
                        value={updatedInfo?.password ?? '*****'}
                        id="password"
                        name="password"
                        type="password"
                        placeholder={t(
                          'account_settings.info_card.placeholder_pass'
                        )}
                        handleChange={handleChangeValues}
                        label={t('account_settings.info_card.password')}
                        error={!!errors.password}
                        helperText={errors.password && errors.password.message}
                        endAdornment={userHasAuthorization('users:write')}
                        {...register('password')}
                        autoComplete="new-password"
                        InputLabelProps={{ shrink: true }}
                      />
                        )}
                  </Grid>
                </Grid>
                {event === 'new' && (
                  <Grid container mt={2}>
                    <Grid item xs={12}>
                      <FreeFormInput
                        className={classes.redAsterisk}
                        required
                        value={updatedInfo?.passwordConfirm ?? ''}
                        id="passwordConfirm"
                        name="passwordConfirm"
                        type="password"
                        handleChange={handleChangeValues}
                        placeholder={t(
                          'account_settings.info_card.placeholder_repass'
                        )}
                        label={t('account_settings.info_card.password_confirm')}
                        error={!!errors.passwordConfirm}
                        endAdornment={true}
                        helperText={
                          errors.passwordConfirm &&
                          errors.passwordConfirm.message
                        }
                        {...register('passwordConfirm')}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                )}
                <Box pt={1} hidden={!errorMessage}>
                  <Typography align={'left'} className={classes.errorMessage}>
                    {props.errorMessage
                      ? t('account_settings.form.' + props.errorMessage)
                      : errorMessage}
                  </Typography>
                </Box>
              </Grid>
              <div className={classes.footer}>
                {event !== 'new' && !disableRole && (
                  <Button
                    className={classes.deleteButton}
                    onClick={() => {
                      setOpenDeleteAlert(true)
                    }}
                    disabled={disableDeleteUser}
                  >
                    {t('account_settings.form.delete')}
                  </Button>
                )}
                <RoundedButton
                  type="submit"
                  size="small"
                  variant="contained"
                  disabled={!enableSave}
                >
                  {' '}
                  {event === 'new'
                    ? t('company_settings.bes_notifications_panel.create')
                    : t('account_settings.form.save')}
                </RoundedButton>
              </div>
            </Grid>
          </div>
        </form>
        <ConfirmDialog
          open={openAlert}
          setOpen={setOpenAlert}
          title={t('general.popup.send_invite')}
          message={popupMessage}
          cancellabel={t('general.popup.cancel')}
          acceptlabel={t('general.popup.send')}
          handleAccept={hanldleAccept}
        />
        <ConfirmDialog
          open={openDeleteAlert}
          setOpen={setOpenDeleteAlert}
          title={t('general.delete_user.title')}
          message={t('general.delete_user.message')}
          highlightText={t('general.delete_user.highlight_question')}
          cancellabel={t('general.labels.cancel')}
          acceptlabel={t('general.labels.continue')}
          handleAccept={handleDeleteUser}
        />
        <ConfirmDialog
          open={openConfirm}
          setOpen={setOpenConfirm}
          title={t('general.delete_confirmed.title')}
          message={t('general.delete_confirmed.message').replace(
            '%EMAIL%',
            updatedInfo.email
          )}
          acceptlabel={t('general.labels.continue')}
          handleAccept={() => {
            props.setReloadUsers(true)
            setOpenConfirm(false)
            updateUsers()
            handleClose()
          }}
        />
      </Drawer>
    </div>
  )
}
